import { useTranslation } from 'react-i18next';
import type { PlayersController } from '@/pages/players/usePlayersController';
import { PlayerRow } from './PlayerRow';
import { NewPlayerRow } from './NewPlayerRow';
import { ratingFieldLabel, externalIdFieldLabel } from '@/lib/game-labels';

interface PlayerTableProps {
  ctrl: PlayersController;
  tournamentId: string;
}

export function PlayerTable({ ctrl, tournamentId }: PlayerTableProps) {
  const { t } = useTranslation('director');
  const { t: tc } = useTranslation('common');
  const {
    players,
    showRating,
    showExternalId,
    gameConfig,
    isRegistrationOpen,
    canWithdrawPlayers,
    canAdd,
    tableScrollRef,
  } = ctrl;

  return (
    <div
      ref={tableScrollRef}
      className="hidden md:block md:min-h-0 md:flex-1 overflow-auto rounded border"
      data-testid="player-table"
    >
      <table className="w-full text-left">
        <thead className="sticky top-0 z-10 border-b bg-muted">
          <tr>
            <th className="w-8 px-2 py-2 text-xs font-medium text-muted-foreground">#</th>
            <th className="w-6 px-1 py-2"></th>
            <th className="w-32 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.first_name')}
            </th>
            <th className="w-32 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.last_name')}
            </th>
            {showRating && (
              <th className="w-20 px-2 py-2 text-xs font-medium text-muted-foreground">
                {ratingFieldLabel(gameConfig, tc)}
              </th>
            )}
            {showExternalId && (
              <th className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground">
                {externalIdFieldLabel(gameConfig, tc)}
              </th>
            )}
            <th className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.city')}
            </th>
            <th className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.country')}
            </th>
            <th className="w-28 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.club')}
            </th>
            <th className="w-52 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.status')}
            </th>
            <th className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">
              {t('players_page.table.notes')}
            </th>
            {(isRegistrationOpen || canWithdrawPlayers) && (
              <th className="w-10 px-2 py-2 text-xs font-medium text-muted-foreground"></th>
            )}
          </tr>
        </thead>
        <tbody>
          {players.map((player, playerIndex) => (
            <PlayerRow
              key={player.id}
              player={player}
              playerIndex={playerIndex}
              ctrl={ctrl}
              tournamentId={tournamentId}
            />
          ))}
          {canAdd && <NewPlayerRow ctrl={ctrl} />}
        </tbody>
      </table>
    </div>
  );
}
